import { config } from './config.js';

export interface ApiError extends Error {
  status: number;
  code?: string;
}

export interface MatchHistoryPlayer {
  matchId: string;
  seat: number;
  userId: string;
  finishPlace: number | null;
  firstName: string;
  username: string | null;
  photoUrl: string | null;
}

export interface MatchHistoryRow {
  id: string;
  status: string;
  stakeStars: number;
  playerCount: number;
  loserId: string | null;
  isDraw: boolean;
  createdAt: string;
  finishedAt: string | null;
  players: MatchHistoryPlayer[];
  myDeltaStars: number;
}

export interface MatchDetailRow {
  id: string;
  status: string;
  stakeStars: number;
  playerCount: number;
  loserId: string | null;
  isDraw: boolean;
  seed: string;
  createdAt: string;
  finishedAt: string | null;
  players: MatchHistoryPlayer[];
}

export interface LeaderboardEntry {
  id: string;
  firstName: string;
  username: string | null;
  photoUrl: string | null;
  rating: number;
  totalGames: number;
  totalWins: number;
}

export interface Achievement {
  id: string;
  title: string;
  caption: string;
  current: number;
  target: number;
  unlocked: boolean;
}

export interface AuthExchangeResult {
  token: string;
  user: {
    id: string;
    telegramId: number;
    username: string | null;
    firstName: string;
    photoUrl: string | null;
    rating: number;
    totalGames: number;
    totalWins: number;
    starsBalance: number;
  };
}

let cachedToken: string | null = null;

export function setSessionToken(token: string | null): void {
  cachedToken = token;
}

export function getSessionToken(): string | null {
  return cachedToken;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (cachedToken) headers.set('Authorization', `Bearer ${cachedToken}`);

  const res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const err = new Error(body || `${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  exchangeInitData(initData: string): Promise<AuthExchangeResult> {
    return call<AuthExchangeResult>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    });
  },
  me(): Promise<AuthExchangeResult['user']> {
    return call<AuthExchangeResult['user']>('/me');
  },
  matchHistory(limit = 50): Promise<MatchHistoryRow[]> {
    return call<MatchHistoryRow[]>(`/matches/history?limit=${limit}`);
  },
  matchDetail(id: string): Promise<MatchDetailRow> {
    return call<MatchDetailRow>(`/matches/${id}`);
  },
  leaderboard(): Promise<LeaderboardEntry[]> {
    return call<LeaderboardEntry[]>('/leaderboard');
  },
  referralStats(): Promise<{ userId: string; invitedCount: number; bonusedCount: number }> {
    return call('/me/referral');
  },
  achievements(): Promise<Achievement[]> {
    return call<Achievement[]>('/me/achievements');
  },
  topupInvoice(amount: number): Promise<{ invoiceUrl: string }> {
    return call<{ invoiceUrl: string }>('/billing/topup-invoice', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },
  cryptoBotTopup(asset: string, amount: number): Promise<{
    invoiceUrl: string;
    invoiceId: number;
    starsExpected: number;
  }> {
    return call('/billing/cryptobot/topup', {
      method: 'POST',
      body: JSON.stringify({ asset, amount }),
    });
  },
  cryptoBotWithdraw(asset: string, stars: number): Promise<{
    transferId: number;
    balanceAfter: number;
    assetAmount: string;
  }> {
    return call('/billing/cryptobot/withdraw', {
      method: 'POST',
      body: JSON.stringify({ asset, stars }),
    });
  },
  tonDepositIntent(expectedTon: number): Promise<{
    address: string;
    comment: string;
    expectedStars: number;
  }> {
    return call('/billing/ton/deposit-intent', {
      method: 'POST',
      body: JSON.stringify({ expectedTon }),
    });
  },
  tonWithdraw(toAddress: string, stars: number): Promise<{
    seqno: number;
    balanceAfter: number;
    amountTon: string;
  }> {
    return call('/billing/ton/withdraw', {
      method: 'POST',
      body: JSON.stringify({ toAddress, stars }),
    });
  },
  balance(): Promise<{ balance: number }> {
    return call<{ balance: number }>('/billing/balance');
  },
};
